<template>
  <BaseModule
    v-bind="$props"
    class="partners-awards-module"
    data-module="partners-awards"
  >
    <div class="partners">
      <header
        v-if="title"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ title }}
        </component>
      </header>

      <Warning
        v-if="editing"
        text="O módulo não possui nenhum item adicionado"
      >
      </Warning>

      <!-- Slider main container -->
      <div
        ref="swiper"
        class="swiper"
      >
        <!-- Additional required wrapper -->
        <div class="swiper-wrapper">
          <!-- Slides -->
          <div
            v-for="(partner, idx) in partners"
            :key="partner.id || idx"
            class="swiper-slide slider-partners"
          >
            <EditorLink
              :id="instanceId"
              :model="partner.link"
            >
              <LImage
                :src="partner.image"
                :alt="partner.name"
                :title="partner.name"
              />
            </EditorLink>
          </div>
        </div>

        <!-- If we need navigation buttons -->
        <div
          v-if="hasMultiplePartners"
          :class="`swiper-button-prev btn-prev-${instanceId}`"
          :title="$t('prev')"
        ></div>
        <div
          v-if="hasMultiplePartners"
          :class="`swiper-button-next btn-next-${instanceId}`"
          :title="$t('next')"
        ></div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';

import base from '@/mixins/base';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'PartnersAwards',
  components: {
    BaseModule,
    EditorLink,
  },
  mixins: [base],
  props: {
    ...BaseModule.props,
    titleWeight: {
      type: String,
      default: '700', // overrides default
    },
    partners: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      swiper: null,
    };
  },
  computed: {
    hasMultiplePartners() {
      return this.partners.length > 1;
    },
    editing() {
      return typeof this.$parent === 'undefined' && this.partners.length === 0;
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    currentEditableItem() {
      this.slideToActivatedItem();
    },
    partners() {
      this.initSwiper();
    },
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    initSwiper() {
      if (this.swiper) {
        this.swiper.destroy();
      }

      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Autoplay, Navigation],
          autoplay: !this.editing,
          observer: true,
          speed: 500,
          allowTouchMove: false,
          breakpoints: {
            320: {
              slidesPerView: 1,
              slidesPerGroup: 1,
            },
            768: {
              slidesPerView: 3,
              slidesPerGroup: 3,
            },
            1024: {
              slidesPerView: 4,
              slidesPerGroup: 4,
            },
            1200: {
              slidesPerView: 5,
              slidesPerGroup: 5,
            },
            1500: {
              slidesPerView: 6,
              slidesPerGroup: 6,
            },
          },
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });

        this.slideToActivatedItem();
      });
    },
    linkHref(partner) {
      if (typeof partner.link === 'string' && /\S/.test(partner.link)) {
        return partner.link;
      }

      // if the partner doesn't have link, the "a" tag won't be clickable
      return undefined;
    },
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('partners')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );

          if (this.swiper) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            toRemove: ['subtitle'],
          },
          {
            name: 'partners',
            label: 'Parceiros/Prêmios',
            fields: [
              {
                name: 'partners',
                type: 'List',
                minItems: 1,
                itemLabel({ name }) {
                  return name || 'Parceiro/Prêmio {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar parceiro/prêmio',
                fields: [
                  {
                    name: 'name',
                    type: 'ShortText',
                    label: 'Nome',
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem/logo',
                    helpText: 'Dimensões recomendadas: 250x140',
                  },
                  {
                    name: 'link',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['textColor', 'buttonColor', 'buttonTextColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        next: 'Avançar',
        prev: 'Voltar',
      },
      en: {
        next: 'Next',
        prev: 'Prev',
      },
      es: {
        next: 'Avance',
        prev: 'Regresar',
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.swiper {
  .swiper-button-prev,
  .swiper-button-next {
    &::after {
      color: $lb-dark-gray !important;
      font-size: 30px;
    }
  }

  .swiper-button-next {
    right: 0;
  }

  .swiper-button-prev {
    left: 0;
  }
}

.partners-awards-module {
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  .partners {
    h2 {
      text-align: center;
      text-transform: uppercase;
    }

    .slider-partners {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: auto;

      .editor-link .img {
        max-width: 150px;
        max-height: 120px;
      }
    }
  }
}
</style>
